import * as React from 'react';
import {useEffect, useState} from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  Radio,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import Model from "../../model/Model";
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import IconicButton from "./IconicButton";
import {useResources} from "../../stores/ResourceProvider";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import {UserType} from "../../API";
import {globalColors} from "../../styles/globalStyles";
import {isoToLocalDateString} from "../../stores/StoreUtilities";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface IModelMenuProps {
  sx?: any
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | undefined
  value: Model
  primaryId?: string
  models: Model[]
  anchorEl: Element | null | undefined
  sortBy: string
  sortOrder: "asc" | "desc"
  archived: boolean
  onChange(option: Model): void
  onPrimary(option: Model): void
  onEdit?(option: Model): void
  onNew?(option?: Model): void
  onDelete?(option: Model): void
  onArchive?(option: Model, archive: boolean): void
  onClose(): void
  onSort(sortBy: string, sortOrder: string, archived: boolean): void
}

export const ClientModelMax = 4

export default function ModelMenu(props: IModelMenuProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isAdvisor, setIsAdvisor] = useState<boolean>(false)
  const [canAddModel, setCanAddModel] = useState<boolean>(true)
  const [options, setOptions] = useState<Model[]>([])
  const [showArchived, setShowArchived] = useState<boolean>(false)
  const [archivedCount, setArchivedCount] = useState<number>(0)

  const theme = useTheme()
  const { userStore, accountStore, modelStore, confirm, notify } = useResources()
  const {models, archived} = props

  useEffect(() => {
    if (userStore.isCustomer) {
      // Limit models
      const clientModels = models.filter((model: Model) => !model.advisorCreated)
      const userType = userStore!.user?.userType
      setCanAddModel((userType !== UserType.Free || clientModels.length > 1) && clientModels.length < ClientModelMax)
    }

    const filtered = models.filter((m: Model) => !m.archived)
    setArchivedCount(models.length - filtered.length)
    if (archived) {
      setOptions(models)
    } else {
      setOptions(filtered)
    }
    setShowArchived(archived)

    setIsAdvisor(userStore!.isAdvisorOrAdmin)

    if (props.anchorEl) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [models, archived, userStore.isLoading])

  const styles = {
    checkedIcon: {
      height: 20,
      width: 20,
      color: theme.palette.action.active
    },
    uncheckedIcon: {
      color: theme.palette.action.disabled,
      height: 20,
      width: 20
    },
    primaryIcon: {
      height: 20,
      width: 20,
      color: globalColors.primaryModel
    },
    nonPrimaryIcon: {
      height: 20,
      width: 20,
      color: globalColors.nonPrimaryModel
    },
    heading: {
      fontSize:15,
      whiteSpace:"normal",
      lineHeight:1.1
    },
    value: {
      fontSize:15,
      whiteSpace:"normal",
      lineHeight:1.1
    },
    archivedValue: {
      fontSize:15,
      whiteSpace:"normal",
      lineHeight:1.1,
      opacity: 0.4
    }
  }
  const handleClose = () => {

  }

  const getInitials = (updatedBy: string) => {
    let initials = ""
    if (updatedBy) {
      const lastFirst = updatedBy.split(", ")
      if (lastFirst.length > 1) {
        // Split first name
        const parts = lastFirst[1].split(" ")
        if (parts.length > 3 && parts[1].charAt(0) === "&") {
          initials += parts[0].charAt(0)
          initials += parts[2].charAt(0)
          return initials.toUpperCase()
        } else if (parts.length > 2) {
          initials += parts[0].charAt(0)
          initials += parts[1].charAt(0)
        } else {
          initials += parts[0].charAt(0)
        }
      }
      if (lastFirst.length > 0) {
        // Get last initial
        initials += lastFirst[0].charAt(0)
        if (initials.length < 3) {
          // Split last name
          const parts = lastFirst[0].split("-")
          if (parts.length > 1) {
            initials += parts[1].charAt(0)
          } 
        }
      }        
    }
    return initials.toUpperCase()
  }

  const renderHeadings = () => {
    const minWidth = 260

    return (
      <Box display="flex" flexDirection="row" key="models-heading" pt={0} borderBottom="1px solid rgba(0, 0, 0, 0.12)">
        <Box display="flex" flexGrow={1} minWidth={minWidth} flexDirection="row">
          <Box display="flex" width={64}></Box>
          <Box display="flex" flexGrow={0} width={338}>
            <IconicButton label="Model Name" labelPosition="left" justifyContent="flex-start"
                          labelSize={15}
                          labelWeight={props.sortBy === "name" ? 700 : 400}
                          onClick={(event: any) => {
                            props.onSort("name", props.sortOrder === "asc" ? "desc" : "asc", showArchived)
                          }}>
              {props.sortBy === "name" && props.sortOrder === "asc" &&
                <ArrowDropUpIcon/>
              }
              {props.sortBy === "name" && props.sortOrder === "desc" &&
                <ArrowDropDownIcon/>
              }
            </IconicButton>
          </Box>
          <Box display="flex" flexGrow={0} width={120} alignItems="center" justifyContent="flex-start">
            <IconicButton label="Updated At" labelPosition="left" justifyContent="flex-start"
                          labelSize={15}
                          labelWeight={props.sortBy === "updatedAt" ? 700 : 400}
                          onClick={(event: any) => {
                            props.onSort("updatedAt", props.sortOrder === "asc" ? "desc" : "asc", showArchived)
                          }}>
              {props.sortBy === "updatedAt" && props.sortOrder === "asc" &&
                <ArrowDropUpIcon/>
              }
              {props.sortBy === "updatedAt" && props.sortOrder === "desc" &&
                <ArrowDropDownIcon/>
              }
            </IconicButton>
          </Box>
          {isAdvisor &&
            <Box display="flex" flexGrow={0} width={48} alignItems="center" justifyContent="flex-start" pl="2px" pt={0.5}>
              <Typography variant="body2" sx={styles.heading}>
                By
              </Typography>
            </Box>
          }
          {isAdvisor &&
            <Box display="flex" flexGrow={0} width={60} alignItems="center" justifyContent="flex-start" pt={0.5}>
              <Typography variant="body2" sx={styles.heading}>
                Sharing
              </Typography>
            </Box>
          }
          <Box width={32}></Box>
        </Box>
      </Box>
    )
  }

  const renderOption = (option: Model) => {
    const { value, primaryId } = props
    const primary = option.id === primaryId
    const current = option.id === value.id
    const minWidth = 260
    const valueStyle = option.archived ? styles.archivedValue : styles.value
    const bgColor = current ? "rgba(31, 92, 167, 0.08)" : undefined

    return (
      <Box display="flex" flexDirection="row" key={option.id} py={0} my={0} bgcolor={bgColor}>
        <Box display="flex" flexGrow={0} width={400} minWidth={minWidth} flexDirection="row">
          <Tooltip title={primary ? "Primary Model" : "Set Primary Model"}>
            <Radio checked={primary}
                   checkedIcon={<StarIcon sx={styles.primaryIcon}/>}
                   icon={<StarBorderIcon sx={styles.nonPrimaryIcon}/>}
                   onChange={(event: any) => {
                     if (event.target.checked) {
                       props.onPrimary(option)
                     }}}
                   sx={{pr:1, mb:"4px", maxHeight:24}}
            />
          </Tooltip>
          <FormControlLabel
            control={
              <Tooltip title={current ? "Visible Model" : "Set Visible Model"}>
                <Radio checked={current}
                              checkedIcon={<VisibilityIcon sx={styles.checkedIcon}/>}
                              icon={<VisibilityOffIcon sx={styles.uncheckedIcon}/>}
                              onChange={(event: any) => {
                                if (event.target.checked) {
                                  props.onChange(option)
                                }}}
                              onClick={(event: any) => handleClose()}
                              sx={{pt:"0px", pb:"0px"}}
                />
              </Tooltip>
            }
            label={
              <Typography variant="body2" sx={{...valueStyle, pt:"4px"}}>{option.name}</Typography>
            }
          />
        </Box>
        {isAdvisor &&
          <Box display="flex" flexGrow={0} width={116} alignItems="center" justifyContent="flex-end">
            <Typography variant="body2" sx={valueStyle}>{isoToLocalDateString(option.updatedAt, "MM/dd/yy hh:mmaaaaa")}</Typography>
          </Box>
        }
        {isAdvisor &&
          <Box display="flex" flexGrow={0} width={48} alignItems="center" justifyContent="flex-start" pl={1}>
            <Typography variant="body2" sx={valueStyle}>{getInitials(option.updatedBy)}</Typography>
          </Box>
        }
        {isAdvisor &&
          <Box display="flex" flexGrow={0} width={60} alignItems="center" justifyContent="flex-start" pl={"6px"}>
            <Typography variant="body2" sx={valueStyle}>
              {Model.getClientAccessLabel(option.clientAccess)}
            </Typography>
          </Box>
        }
        <Box display="flex" flexGrow={1} alignItems="center" justifyContent="flex-end">
          <Box width={32}>
            <Tooltip title="Edit" placement="top">
              <IconButton id={`${option.id}-edit-btn`} disabled={!modelStore.canEdit(option)} size="small"
                          sx={{paddingTop:0, paddingBottom:"6px"}}
                          onClick={(event: any) => {
                                    if (props.onEdit) {
                                      props.onEdit(option)
                                  }}}
              >
                <EditIcon sx={{width:18, height:18}}/>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Popover
      open={ isOpen }
      id="model-menu-popover"
      anchorEl={ props.anchorEl }
      disableRestoreFocus
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={ () => { // Needed for handling backdrop clicks.
        setIsOpen(false)
        props.onClose()
      }}
      sx={{
        mt: "-8px",
        ml: "-4px",
        maxWidth: !isAdvisor ? "360px" : undefined
      }}
    >
      <Box display="flex" flexDirection="column" width="100%" p={0} mt={0} sx={{zIndex: theme.zIndex.drawer + 1}}>
        {isAdvisor && renderHeadings()}
        {options.map((option: Model) =>
          renderOption(option)
        )}
        <Box display="flex" key="model-spacer" minHeight={28}>
        </Box>
        {canAddModel &&
          <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" pl={5} py={0} bgcolor="rgb(236, 239, 241)">
            <Box display="flex" flexGrow={0} width={360}>
              <IconicButton label="New Model" labelPosition="right"
                            onClick={(event: any) => {
                              handleClose()
                              if (props.onNew) {
                                props.onNew(new Model({}))
                              }}}>
                <AddCircleIcon sx={{color: theme.palette.secondary.light, paddingRight:"2px"}}/>
              </IconicButton>
            </Box>
            {isAdvisor &&
              <FormControlLabel value={showArchived} sx={{pt:"4px", maxHeight:"36px"}}
                                label={<Typography variant="body2">Show Archived ({archivedCount})</Typography>}
                                control={
                                  <Checkbox name="showArchived" value={true}
                                            checked={showArchived}
                                            icon={<CheckCircleOutlineIcon />}
                                            checkedIcon={<CheckCircleIcon />}
                                            onClick={(event: any) => {
                                              props.onSort(props.sortBy, props.sortOrder, event.target.checked)
                                            }}
                                  />}
              />
            }
          </Box>
        }
      </Box>
    </Popover>
  );
}
